/* eslint-disable react/jsx-no-bind */
import * as menuBoard from "../../lib/api/menuBoard";
import * as menuBoardBadge from "../../enums/menuBoardBadge";
import * as menuBoardTemplate from "../../enums/menuBoardTemplate";
import * as menuBoardType from "../../enums/menuBoardType";
import AddEditMenuBoardItemModal from "../AddEditMenuBoardItemModal";
import AddFeatureAssetModal from "../AddFeatureAssetModal";
import Loader from "../Loader";
import logger from "../../lib/logger";
import PropTypes from "prop-types";
import StyledDropdown from "../StyledDropdown";
import React, { PureComponent } from "react";
import {
    Button,
    Grid,
    Row,
    Col,
    FormGroup,
    FormControl,
    ControlLabel,
    Table,
} from "react-bootstrap";
import { css, StyleSheet } from "aphrodite";
import PreviewMediaModal from "../PreviewMediaModal";

const styles = StyleSheet.create({
    description: {
        marginTop: "12px",
        marginBottom: "12px",
    },
    boldText: {
        fontWeight: 500,
    },
    flexRow: {
        display: "flex",
        flexDirection: "row",
        gap: "30px",
        alignItems: "center",
    },
    flex1: {
        flex: 1,
    },
    addButton: {
        height: "fit-content",
    },
    addMedia: {
        marginBottom: "16px",
    },
});

const strings = {
    title: "Edit Menu Board Section | Habit CMS",
    menuBoard: menuName => `Menu Board - ${menuName} - Edit Section`,
    boardSection: "Board Section",
    menuCategory: "Menu Category",
    templateName: "Template Name",
    preview: "Preview",
    save: "Save",
    dmbCategoryTitle: "DMB Category Title",
    dmbCategorySubtitle: "DMB Category Subtitle",
    dmbCategoryDescription: "DMB Category Description",
    items: "Items",
    itemsPlaceholder: "Select an item to add",
    itemGroupings: "Item Groupings",
    groupingsPlaceholder: "Select a grouping to add",
    add: "Add",
    product: "Product",
    plu: "PLU",
    dmbName: "DMB Name",
    dmbDescription: "DMB Description",
    badge: "Badge",
    sort: "Sort",
    edit: "Edit",
    delete: "Delete",
    unexpectedReorderError:
        "An unexpected error occurred while reordering the product. Please try again.",
    name: "Name",
    media: "Media",
    description: "Description",
    addMedia: "Add Media",
    disclaimerText: "Disclaimer Text",
};

function getTemplateName(template, width, height) {
    switch (template) {
        case menuBoardTemplate.category_product_full_description:
            return "Category Product - Full Description";
        case menuBoardTemplate.category_product_name_price_cal:
            return "Category - Products with name, price, and calories only";
        case menuBoardTemplate.category_hide_products:
            return "Category - Hide items";
        case menuBoardTemplate.category_product_as_option:
            return "Category - Products as Options";
        case menuBoardTemplate.category_with_ad_logo_small:
            return "Category - With ad logo";
        case menuBoardTemplate.feature:
            return `Marketing Space - ${width} x ${height}`;
        case menuBoardTemplate.legend:
            return "Icon Legend";
        case menuBoardTemplate.disclaimer:
            return "Disclaimer";
        default:
            return "";
    }
}

function getTemplateDescription(template) {
    switch (template) {
        case menuBoardTemplate.category_product_full_description:
            return 'This template shows the name, calories, price, description, and product badges for items and item groupings. It assumes any item groupings have 2 items and for these groupings it will display the price of each and the name of the increased item (such as "with cheese"). This template was designed with 6 items/groupings in mind, but should work well for 4-8. This template also supports displaying modifiers (such as "swap" and "add"), but those are configured in code and are not currently configurable.';
        case menuBoardTemplate.category_product_name_price_cal:
            return "This template shows the name, calories, and price of each item. If all prices are the same, they are shown next to the category title. If they vary, they are shown by each item. Product descriptions are not shown in this template.";
        case menuBoardTemplate.category_hide_products:
            return "This template shows the category name, subtitle, and price of the items available. If all items are the same price, one price is shown. If the prices vary, the prices show as a range.";
        case menuBoardTemplate.category_product_as_option:
            return "This template shows the name, calories, and price of each item. If all prices are the same, they are shown next to the category title. If they vary, they are shown by each item. Product descriptions are not shown in this template.";
        case menuBoardTemplate.category_with_ad_logo_small:
            return "This template shows the name, calories, and price of each item. It also shows the DMB description, as well as a Pepsi logo.";
        case menuBoardTemplate.feature:
            return "This template displays only the media uploaded in the table below. It cycles through all of the assets at full size within the template section.";
        case menuBoardTemplate.disclaimer:
            return "This template only shows the disclaimer text exactly as it is entered here.";
        case menuBoardTemplate.legend:
        default:
            return "";
    }
}

function getBadgeName(badge) {
    switch (badge) {
        case menuBoardBadge.fan_favorite.value:
            return menuBoardBadge.fan_favorite.label;
        case menuBoardBadge.new_and_improved.value:
            return menuBoardBadge.new_and_improved.label;
        case menuBoardBadge.vegetarian.value:
            return menuBoardBadge.vegetarian.label;
        case menuBoardBadge.none.value:
        default:
            return "";
    }
}

export default class EditMenuBoardSectionPage extends PureComponent {
    static propTypes = {
        match: PropTypes.object.isRequired,
        location: PropTypes.object.isRequired,
    };

    state = {
        loading: true,
        id: "",
        name: "",
        template: "",
        type: "",
        categoryData: {},
        featureData: {},
        disclaimerText: "",
        menuName: "",
        width: undefined,
        height: undefined,
        selectedItem: null,
        selectedGrouping: null,
        titleInput: "",
        subtitleInput: "",
        descriptionInput: "",
        isCategoryEdited: false,
        isDisclaimerEdited: false,
        disclaimerTextInput: "",
        // Add/Edit Modal
        editingItemGrouping: undefined,
        editingPlu: undefined,
        editingDmbName: undefined,
        editingDmbBadge: undefined,
        editingDmbDescription: undefined,
        editingItemId: undefined,
        editingGroupingId: undefined,
        editingProductId: undefined,
        editingShowDisclaimer: undefined,
    };

    componentDidMount() {
        this._isMounted = true;
        document.title = strings.title;

        const locationState = this.props.location.state;

        menuBoard
            .getMenuBoardSection(this.props.match.params.sectionId)
            .then(sectionResp => {
                if (this._isMounted) {
                    this.setState({
                        id: sectionResp.id,
                        name: sectionResp.name,
                        template: sectionResp.template,
                        type: sectionResp.type,
                        categoryData: sectionResp.categoryData,
                        featureData: sectionResp.featureData,
                        disclaimerText: sectionResp.disclaimerText,
                        loading: false,
                        menuName: locationState ? locationState.menuName : "",
                        width: locationState ? locationState.width : undefined,
                        height: locationState ? locationState.height : undefined,
                        titleInput: sectionResp.categoryData ? sectionResp.categoryData.title : "",
                        subtitleInput: sectionResp.categoryData
                            ? sectionResp.categoryData.subtitle
                            : "",
                        descriptionInput: sectionResp.categoryData
                            ? sectionResp.categoryData.description
                            : "",
                        disclaimerTextInput: sectionResp.disclaimerText,
                    });
                }
                if (sectionResp.categoryData) {
                    // Get category data if this is the edit page for a category
                    return menuBoard.getMenuBoardCategoryProducts(
                        sectionResp.categoryData.categoryId,
                    );
                }
                return Promise.resolve();
            })
            .then(productsResp => {
                if (this._isMounted && productsResp) {
                    this.setState({
                        items: productsResp.items,
                        combos: productsResp.combos,
                        groupings: productsResp.groupings,
                    });
                }
            })
            .catch(err => {
                logger.warn(err);
                if (this._isMounted) {
                    this.setState({ loading: false });
                }
            });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    _onSaveCategory = () => {
        menuBoard
            .editMenuBoardCategory(this.state.id, {
                title: this.state.titleInput,
                subtitle: this.state.subtitleInput,
                description: this.state.descriptionInput,
            })
            .then(() => {
                this.setState(prevState => {
                    return {
                        categoryData: {
                            ...prevState.categoryData,
                            title: this.state.titleInput,
                            subtitle: this.state.subtitleInput,
                            description: this.state.descriptionInput,
                        },
                        isCategoryEdited: false,
                    };
                });
            });
    };

    _handleTitleChange = e => {
        this.setState({ titleInput: e.target.value, isCategoryEdited: true });
    };

    _handleSubtitleChange = e => {
        this.setState({ subtitleInput: e.target.value, isCategoryEdited: true });
    };

    _handleDescriptionChange = e => {
        this.setState({ descriptionInput: e.target.value, isCategoryEdited: true });
    };

    _handleChangeItemsDropdown = value => {
        const selectedItem = this.state.items.find(item => item.name === value);
        this.setState({ selectedItem });
    };

    _handleChangeGroupingsDropdown = value => {
        const selectedGrouping = this.state.groupings.find(grouping => grouping.name === value);
        this.setState({ selectedGrouping });
    };

    _onAddItem = () => {
        this.setState({
            editingItemGrouping: this.state.selectedItem.name,
            editingPlu: this.state.selectedItem.plu,
            editingDmbName: undefined,
            editingDmbBadge: undefined,
            editingDmbDescription: undefined,
            editingItemId: this.state.selectedItem.id,
            editingGroupingId: undefined,
            editingProductId: undefined,
            editingShowDisclaimer: undefined,
        });
    };

    _onAddGrouping = () => {
        this.setState({
            editingItemGrouping: this.state.selectedGrouping.name,
            editingPlu: this.state.selectedGrouping.plu,
            editingDmbName: undefined,
            editingDmbBadge: undefined,
            editingDmbDescription: undefined,
            editingItemId: undefined,
            editingGroupingId: this.state.selectedGrouping.id,
            editingProductId: undefined,
            editingShowDisclaimer: undefined,
        });
    };

    _onEditProduct = product => {
        this.setState({
            editingItemGrouping: product.productName,
            editingPlu: product.plu,
            editingDmbName: product.menuBoardName,
            editingDmbBadge: product.badge,
            editingDmbDescription: product.menuBoardDescription,
            editingProductId: product.id,
            editingShowDisclaimer: product.showDisclaimer,
            editingItemId: undefined,
            editingGroupingId: undefined,
        });
    };

    _onDeleteProduct = productId => {
        menuBoard
            .deleteMenuBoardCategoryProduct(productId)
            .then(() => {
                this.setState(prevState => {
                    return {
                        categoryData: {
                            ...prevState.categoryData,
                            products: prevState.categoryData.products.filter(
                                product => product.id !== productId,
                            ),
                        },
                    };
                });
            })
            .catch(error => {
                alert("An error occurred: " + error);
            });
    };

    _handleCloseAddEditMenuBoardItemModal = () => {
        this.setState({ editingItemGrouping: undefined });
    };

    _reorderProduct = (product, currentSortOrder, newSortOrder) => {
        menuBoard
            .reorder(product.id, currentSortOrder, newSortOrder)
            .then(() => {
                if (this._isMounted) {
                    const newProducts = this.state.categoryData.products.filter(
                        p => p.id !== product.id,
                    );
                    newProducts.splice(newSortOrder, 0, product);
                    this.setState(prevState => {
                        return {
                            categoryData: { ...prevState.categoryData, products: newProducts },
                        };
                    });
                }
            })
            .catch(e => {
                logger.warn(e);
                alert(strings.unexpectedReorderError);
            });
    };

    _onAddMedia = () => {
        this.setState({
            addingFeatureAsset: true,
        });
    };

    _handleCloseFeatureAssetModal = () => {
        this.setState({ addingFeatureAsset: undefined });
    };

    _reorderAsset = (asset, currentSortOrder, newSortOrder) => {
        menuBoard
            .reorderAsset(asset.id, currentSortOrder, newSortOrder)
            .then(() => {
                if (this._isMounted) {
                    const newAssets = this.state.featureData.assets.filter(a => a.id !== asset.id);
                    newAssets.splice(newSortOrder, 0, asset);
                    this.setState(prevState => {
                        return {
                            featureData: { ...prevState.featureData, assets: newAssets },
                        };
                    });
                }
            })
            .catch(e => {
                logger.warn(e);
                alert(strings.unexpectedReorderError);
            });
    };

    _onDeleteAsset = assetId => {
        menuBoard
            .deleteMenuBoardFeatureAsset(assetId)
            .then(() => {
                this.setState(prevState => {
                    return {
                        featureData: {
                            ...prevState.featureData,
                            assets: prevState.featureData.assets.filter(
                                asset => asset.id !== assetId,
                            ),
                        },
                    };
                });
            })
            .catch(error => {
                alert("An error occurred: " + error);
            });
    };

    _handleClosePreviewMediaModal = () => {
        this.setState({ previewMediaId: undefined });
    };

    _handleDisclaimerTextChange = e => {
        this.setState({ disclaimerTextInput: e.target.value, isDisclaimerEdited: true });
    };

    _onSaveDisclaimer = () => {
        menuBoard
            .editMenuBoardDisclaimer(this.state.id, {
                text: this.state.disclaimerTextInput,
            })
            .then(() => {
                this.setState(prevState => {
                    return {
                        disclaimerText: prevState.disclaimerTextInput,
                        isDisclaimerEdited: false,
                    };
                });
            });
    };

    _onSaveAddAsset = newAsset => {
        this.setState(prevState => {
            const newAssets = prevState.featureData.assets;
            newAssets.push(newAsset);
            return {
                featureData: {
                    ...prevState.featureData,
                    assets: newAssets,
                },
            };
        });
        this._handleCloseFeatureAssetModal();
    };

    render() {
        const previewedAsset =
            this.state.featureData && this.state.featureData.assets
                ? this.state.featureData.assets.find(a => a.id === this.state.previewMediaId)
                : undefined;
        let previewedAssetUrl = undefined;
        if (previewedAsset) {
            previewedAssetUrl =
                previewedAsset.type === "image"
                    ? previewedAsset.data.images[0].url
                    : previewedAsset.data.url;
        }

        return (
            <Grid>
                <Row>
                    <h3>{strings.menuBoard(this.state.menuName)}</h3>
                </Row>
                <Row>
                    <Col md={3}>
                        <div className={css(styles.boldText)}>{strings.boardSection}</div>
                        <div>{this.state.name}</div>
                    </Col>
                    <Col md={3}>
                        <div className={css(styles.boldText)}>{strings.menuCategory}</div>
                        {this.state.type === menuBoardType.feature ||
                        this.state.type === menuBoardType.disclaimer ? (
                            <div>-</div>
                        ) : (
                            <div>{this.state.categoryData.name}</div>
                        )}
                    </Col>
                    <Col md={3}>
                        <div className={css(styles.boldText)}>{strings.templateName}</div>
                        <div>
                            {getTemplateName(
                                this.state.template,
                                this.state.width,
                                this.state.height,
                            )}
                        </div>
                    </Col>
                    <Col md={3}>
                        {/* TODO: add preview button */}
                        {(this.state.type === menuBoardType.category ||
                            this.state.type === menuBoardType.disclaimer) && (
                            <Button
                                bsStyle="primary"
                                onClick={
                                    this.state.type === menuBoardType.category
                                        ? this._onSaveCategory
                                        : this._onSaveDisclaimer
                                }
                                disabled={
                                    this.state.type === menuBoardType.category
                                        ? !this.state.isCategoryEdited
                                        : !this.state.isDisclaimerEdited
                                }
                            >
                                {strings.save}
                            </Button>
                        )}
                    </Col>
                </Row>
                <Row>
                    <div className={css(styles.description)}>
                        {getTemplateDescription(this.state.template)}
                    </div>
                </Row>
                {this.state.type === menuBoardType.feature && (
                    <div>
                        <Button
                            bsStyle="primary"
                            className={css(styles.addMedia)}
                            onClick={this._onAddMedia}
                        >
                            {strings.addMedia}
                        </Button>
                        <Row>
                            <Col md={12}>
                                <Table striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th>{strings.name}</th>
                                            <th>{strings.media}</th>
                                            <th>{strings.description}</th>
                                            <th>{strings.sort}</th>
                                            <th>{strings.delete}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.state.loading
                                            ? null
                                            : this.state.featureData.assets.map((asset, index) => (
                                                  <tr key={asset.id}>
                                                      <td>{asset.name}</td>
                                                      <td>
                                                          <Button
                                                              bsStyle="link"
                                                              onClick={() => {
                                                                  this.setState({
                                                                      previewMediaId: asset.id,
                                                                  });
                                                              }}
                                                          >
                                                              {strings.preview}
                                                          </Button>
                                                      </td>
                                                      <td>{asset.description}</td>
                                                      <td>
                                                          {
                                                              <div>
                                                                  {index === 0 ? null : (
                                                                      <Button
                                                                          onClick={() =>
                                                                              this._reorderAsset(
                                                                                  asset,
                                                                                  index,
                                                                                  index - 1,
                                                                              )
                                                                          }
                                                                      >
                                                                          &#9650;
                                                                      </Button>
                                                                  )}
                                                                  {index ===
                                                                  this.state.featureData.assets
                                                                      .length -
                                                                      1 ? null : (
                                                                      <Button
                                                                          onClick={() =>
                                                                              this._reorderAsset(
                                                                                  asset,
                                                                                  index,
                                                                                  index + 1,
                                                                              )
                                                                          }
                                                                      >
                                                                          &#9660;
                                                                      </Button>
                                                                  )}
                                                              </div>
                                                          }
                                                      </td>
                                                      <td>
                                                          <Button
                                                              bsStyle="primary"
                                                              onClick={() => {
                                                                  this._onDeleteAsset(asset.id);
                                                              }}
                                                          >
                                                              {strings.delete}
                                                          </Button>
                                                      </td>
                                                  </tr>
                                              ))}
                                    </tbody>
                                </Table>
                            </Col>
                        </Row>
                        {this.state.addingFeatureAsset ? (
                            <AddFeatureAssetModal
                                show={!!this.state.addingFeatureAsset}
                                handleClose={this._handleCloseFeatureAssetModal}
                                onSaveCallback={this._onSaveAddAsset}
                                sectionId={this.state.id}
                                menuName={this.state.menuName}
                                width={this.state.width}
                                height={this.state.height}
                            />
                        ) : null}
                        {!!previewedAsset && !!previewedAssetUrl ? (
                            <PreviewMediaModal
                                show={!!this.state.previewMediaId}
                                handleClose={this._handleClosePreviewMediaModal}
                                mediaType={previewedAsset.type}
                                url={previewedAssetUrl}
                            />
                        ) : null}
                    </div>
                )}
                {this.state.type === menuBoardType.disclaimer && (
                    <Row>
                        <Col md={12}>
                            <FormGroup controlId="disclaimer">
                                <ControlLabel>{strings.disclaimerText}</ControlLabel>
                                <FormControl
                                    type="text"
                                    value={this.state.disclaimerTextInput}
                                    onChange={this._handleDisclaimerTextChange}
                                    componentClass="textarea"
                                    rows={3}
                                />
                            </FormGroup>
                        </Col>
                    </Row>
                )}
                {this.state.type === menuBoardType.category && (
                    <div>
                        <Row>
                            <Col md={6}>
                                <FormGroup controlId="title">
                                    <ControlLabel>{strings.dmbCategoryTitle}</ControlLabel>
                                    <FormControl
                                        type="text"
                                        value={this.state.titleInput}
                                        onChange={this._handleTitleChange}
                                    />
                                </FormGroup>
                            </Col>
                            <Col md={6}>
                                <FormGroup controlId="subtitle">
                                    <ControlLabel>{strings.dmbCategorySubtitle}</ControlLabel>
                                    <FormControl
                                        type="text"
                                        value={this.state.subtitleInput}
                                        onChange={this._handleSubtitleChange}
                                        componentClass="textarea"
                                        rows={3}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <FormGroup controlId="description">
                                    <ControlLabel>{strings.dmbCategoryDescription}</ControlLabel>
                                    <FormControl
                                        type="text"
                                        value={this.state.descriptionInput}
                                        onChange={this._handleDescriptionChange}
                                        componentClass="textarea"
                                        rows={3}
                                    />
                                </FormGroup>
                            </Col>
                        </Row>
                    </div>
                )}
                <Row>
                    <Col md={12} className={css(styles.flexRow)}>
                        {this.state.items && this.state.items.length > 0 && (
                            <div className={css(styles.flexRow, styles.flex1)}>
                                <FormGroup controlId="items" className={css(styles.flex1)}>
                                    <ControlLabel>{strings.items}</ControlLabel>
                                    <StyledDropdown
                                        id="items"
                                        value={
                                            this.state.selectedItem
                                                ? this.state.selectedItem.name
                                                : ""
                                        }
                                        onChange={this._handleChangeItemsDropdown}
                                        options={[
                                            ...this.state.items.map(item => ({
                                                value: item.name,
                                            })),
                                        ]}
                                        placeholder={strings.itemsPlaceholder}
                                    />
                                </FormGroup>
                                <Button
                                    bsStyle="primary"
                                    disabled={this.state.selectedItem === null}
                                    className={css(styles.addButton)}
                                    onClick={this._onAddItem}
                                >
                                    {strings.add}
                                </Button>
                            </div>
                        )}
                        {this.state.groupings && this.state.groupings.length > 0 && (
                            <div className={css(styles.flexRow, styles.flex1)}>
                                <FormGroup controlId="groupings" className={css(styles.flex1)}>
                                    <ControlLabel>{strings.itemGroupings}</ControlLabel>
                                    <StyledDropdown
                                        id="groupings"
                                        value={
                                            this.state.selectedGrouping
                                                ? this.state.selectedGrouping.name
                                                : ""
                                        }
                                        onChange={this._handleChangeGroupingsDropdown}
                                        options={[
                                            ...this.state.groupings.map(grouping => ({
                                                value: grouping.name,
                                            })),
                                        ]}
                                        placeholder={strings.groupingsPlaceholder}
                                    />
                                </FormGroup>
                                <Button
                                    bsStyle="primary"
                                    disabled={this.state.selectedGrouping === null}
                                    className={css(styles.addButton)}
                                    onClick={this._onAddGrouping}
                                >
                                    {strings.add}
                                </Button>
                            </div>
                        )}
                    </Col>
                </Row>
                {this.state.type === menuBoardType.category && (
                    <Row>
                        <Col md={12}>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>{strings.product}</th>
                                        <th>{strings.plu}</th>
                                        <th>{strings.dmbName}</th>
                                        <th>{strings.dmbDescription}</th>
                                        <th>{strings.badge}</th>
                                        <th>{strings.sort}</th>
                                        <th>{strings.edit}</th>
                                        <th>{strings.delete}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.state.loading
                                        ? null
                                        : this.state.categoryData.products.map((product, index) => (
                                              <tr key={product.id}>
                                                  <td>{product.productName}</td>
                                                  <td>{product.plu}</td>
                                                  <td>
                                                      {product.menuBoardName
                                                          ? product.menuBoardName
                                                          : null}
                                                  </td>
                                                  <td>
                                                      {product.menuBoardDescription
                                                          ? product.menuBoardDescription
                                                          : null}
                                                  </td>
                                                  <td>{getBadgeName(product.badge)}</td>
                                                  <td>
                                                      {
                                                          <div>
                                                              {index === 0 ? null : (
                                                                  <Button
                                                                      onClick={() =>
                                                                          this._reorderProduct(
                                                                              product,
                                                                              index,
                                                                              index - 1,
                                                                          )
                                                                      }
                                                                  >
                                                                      &#9650;
                                                                  </Button>
                                                              )}
                                                              {index ===
                                                              this.state.categoryData.products
                                                                  .length -
                                                                  1 ? null : (
                                                                  <Button
                                                                      onClick={() =>
                                                                          this._reorderProduct(
                                                                              product,
                                                                              index,
                                                                              index + 1,
                                                                          )
                                                                      }
                                                                  >
                                                                      &#9660;
                                                                  </Button>
                                                              )}
                                                          </div>
                                                      }
                                                  </td>
                                                  <td>
                                                      <Button
                                                          bsStyle="primary"
                                                          onClick={() => {
                                                              this._onEditProduct(product);
                                                          }}
                                                      >
                                                          {strings.edit}
                                                      </Button>
                                                  </td>
                                                  <td>
                                                      <Button
                                                          bsStyle="primary"
                                                          onClick={() => {
                                                              this._onDeleteProduct(product.id);
                                                          }}
                                                      >
                                                          {strings.delete}
                                                      </Button>
                                                  </td>
                                              </tr>
                                          ))}
                                </tbody>
                            </Table>
                        </Col>
                    </Row>
                )}
                <Row>
                    <Col md={12}>{this.state.loading ? <Loader /> : null}</Col>
                </Row>
                {this.state.editingItemGrouping ? (
                    <AddEditMenuBoardItemModal
                        itemGrouping={this.state.editingItemGrouping}
                        plu={this.state.editingPlu}
                        dmbName={this.state.editingDmbName}
                        dmbBadge={this.state.editingDmbBadge}
                        dmbDescription={this.state.editingDmbDescription}
                        menuName={this.state.menuName}
                        sectionId={this.state.id}
                        itemId={this.state.editingItemId}
                        groupingId={this.state.editingGroupingId}
                        productId={this.state.editingProductId}
                        showDisclaimer={this.state.editingShowDisclaimer}
                        show={!!this.state.editingItemGrouping}
                        handleClose={this._handleCloseAddEditMenuBoardItemModal}
                        // eslint-disable-next-line react/jsx-no-bind
                        onSaveCallback={newProduct => {
                            // Update products table
                            this.setState(prevState => {
                                // Add
                                if (this.state.editingProductId === undefined) {
                                    const newProducts = prevState.categoryData.products;
                                    newProducts.push(newProduct);
                                    return {
                                        categoryData: {
                                            ...prevState.categoryData,
                                            products: newProducts,
                                            id: prevState.tempProductId,
                                        },
                                        tempProductId: prevState.tempProductId + 1,
                                    };
                                }
                                // Edit
                                return {
                                    categoryData: {
                                        ...prevState.categoryData,
                                        products: prevState.categoryData.products.map(product => {
                                            if (product.id === newProduct.id) {
                                                return {
                                                    ...newProduct,
                                                    sortOrder: product.sortOrder,
                                                };
                                            }
                                            return product;
                                        }),
                                    },
                                };
                            });
                            this._handleCloseAddEditMenuBoardItemModal();
                        }}
                    />
                ) : null}
            </Grid>
        );
    }
}
